import { useState, useEffect, useMemo, useCallback, type FormEvent } from "react";
import { api } from "../lib/api";
import HelpHint from "../components/HelpHint";
import { isPendingMicrosoftUserOid, type User, type AppRole } from "@domaddress/shared";
import SearchableSelect, { type SearchableSelectOption } from "../components/SearchableSelect";
import BeheerBackupsPanel from "./BeheerBackupsPanel";
import AdminRolesMatrixPanel from "../components/AdminRolesMatrixPanel";
import type { AppRoleDefinition } from "@domaddress/shared";

type AdminUser = User & { contact_first_name?: string; contact_last_name?: string; microsoft_oid: string };

type MedewerkerRolRij = {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  app_role: AppRole;
  linked_user: { id: number; name: string; email: string; microsoft_oid: string } | null;
  /** Unieke sleutel per rij (zelfde kaart kan meerdere Microsoft-accounts hebben). */
  row_key?: string;
};

type UserZonderKoppeling = { id: number; name: string; email: string; app_role: AppRole };

type RoleOverview = {
  roles?: AppRoleDefinition[];
  by_role: Record<string, MedewerkerRolRij[]>;
  assignment_rows?: MedewerkerRolRij[];
  users_without_contact: UserZonderKoppeling[];
};

type Taak = { id: number; name: string; member_count: number };

const ROL_UITLEG: { value: AppRole; label: string; omschrijving: string }[] = [
  {
    value: "deelnemer",
    label: "Deelnemer",
    omschrijving: "Standaard. Kan eigen contacten en groepen zien volgens de bestaande regels.",
  },
  {
    value: "lezer_basis",
    label: "Lezer (basis)",
    omschrijving:
      "Alleen lezen: alle adresboeklijsten; per kaart alleen naam, e-mailadressen en telefoonnummers (geen adres, notities, enz.). Geen bewerken.",
  },
  {
    value: "lezer_volledig",
    label: "Lezer (volledig)",
    omschrijving:
      "Alleen lezen: alle adresboeklijsten en volledige kaartgegevens. Geen importeren, aanmaken of wijzigen.",
  },
  {
    value: "secretaris",
    label: "Secretaris",
    omschrijving: "O.a. leden in groepen beheren (koppelen/ontkoppelen) en groepsstructuur; brede toegang zoals in de app is ingevuld.",
  },
  {
    value: "vrijwilliger_coordinator",
    label: "Vrijwilliger-coördinator",
    omschrijving: "Dominicusgangers (bezoekersbestand): alleen lezen, en dan alleen personen die vrijwilliger willen zijn.",
  },
  {
    value: "beheerder",
    label: "Beheerder",
    omschrijving: "Organisatie-brede beheerder (naast superbeheerder), o.a. groepsleden beheren volgens de rechtenmatrix.",
  },
];

function SvgEdit() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
    </svg>
  );
}

/** Zelfde prullenbak-icoon als op contactkaarten (`btn-icon`). */
function SvgTrashCard() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3,6 5,6 21,6" />
      <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6" />
      <path d="M10,11v6" />
      <path d="M14,11v6" />
      <path d="M9,6V4h6v2" />
    </svg>
  );
}

function SvgTrash() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

function SvgUserOff() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="17" y1="8" x2="22" y2="13" />
      <line x1="22" y1="8" x2="17" y2="13" />
    </svg>
  );
}

function SvgUserMinus() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="17" y1="11" x2="23" y2="11" />
    </svg>
  );
}

export default function AdminUsersPage() {
  const [activeTab, setActiveTab] = useState<"users" | "taken" | "backups">("users");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [overview, setOverview] = useState<RoleOverview | null>(null);
  const [me, setMe] = useState<User | null>(null);
  const [taken, setTaken] = useState<Taak[]>([]);
  const [newTaakNaam, setNewTaakNaam] = useState("");
  const [editTaakId, setEditTaakId] = useState<number | null>(null);
  const [editTaakNaam, setEditTaakNaam] = useState("");
  const [takenBusy, setTakenBusy] = useState(false);
  const [takenError, setTakenError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expectedEmail, setExpectedEmail] = useState("");
  const [expectedName, setExpectedName] = useState("");
  const [expectedAppRole, setExpectedAppRole] = useState<AppRole>("deelnemer");
  const [expectedError, setExpectedError] = useState<string | null>(null);
  const [addingExpected, setAddingExpected] = useState(false);
  const [addRolContactId, setAddRolContactId] = useState("");
  const [addRolChoice, setAddRolChoice] = useState<AppRole>("lezer_basis");
  const [addRolBusy, setAddRolBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, orRes, meRes, takenRes] = await Promise.all([
        api.get<{ data: AdminUser[] }>("/api/admin/users"),
        api.get<RoleOverview>("/api/admin/role-overview"),
        api.get<{ data: User }>("/api/me"),
        api.get<{ data: Taak[] }>("/api/taken"),
      ]);
      setUsers(usersRes.data.data);
      setOverview(orRes.data);
      setMe(meRes.data.data);
      setTaken(takenRes.data.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const APP_ROLES: { value: AppRole; label: string }[] = useMemo(() => {
    if (overview?.roles?.length) {
      return overview.roles.map((r) => ({ value: r.key as AppRole, label: r.name }));
    }
    return ROL_UITLEG.map((r) => ({ value: r.value, label: r.label }));
  }, [overview?.roles]);

  const assignmentRows = useMemo(() => {
    if (overview?.assignment_rows?.length) return overview.assignment_rows;
    if (!overview) return [];
    const rows: MedewerkerRolRij[] = [];
    for (const r of APP_ROLES) {
      rows.push(...(overview.by_role[r.value] ?? []));
    }
    return rows;
  }, [overview, APP_ROLES]);

  /** Alleen iemand met een rol boven Deelnemer (standaard staat niet in deze lijst). */
  const elevatedAssignmentRows = useMemo(
    () => assignmentRows.filter((r) => r.app_role !== "deelnemer"),
    [assignmentRows]
  );

  const rolesToAssign = useMemo(
    () => APP_ROLES.filter((r) => r.value !== "deelnemer"),
    [APP_ROLES]
  );

  /** Medewerkers die nog Deelnemer zijn (één optie per kaart). */
  type CombinedRow =
    | { kind: "user"; user: AdminUser; staff: MedewerkerRolRij | null }
    | { kind: "staff"; staff: MedewerkerRolRij };

  const combinedRows = useMemo((): CombinedRow[] => {
    const staffByUserId = new Map<number, MedewerkerRolRij>();
    for (const row of assignmentRows) {
      if (row.linked_user) staffByUserId.set(row.linked_user.id, row);
    }
    const userRows: CombinedRow[] = users.map((user) => ({
      kind: "user" as const,
      user,
      staff: staffByUserId.get(user.id) ?? null,
    }));
    const staffOnly = elevatedAssignmentRows
      .filter((row) => !row.linked_user)
      .map((staff) => ({ kind: "staff" as const, staff }));
    const all = [...userRows, ...staffOnly];
    all.sort((a, b) => {
      const nameA = a.kind === "user" ? a.user.name : `${a.staff.first_name} ${a.staff.last_name}`;
      const nameB = b.kind === "user" ? b.user.name : `${b.staff.first_name} ${b.staff.last_name}`;
      return nameA.localeCompare(nameB, "nl");
    });
    return all;
  }, [users, assignmentRows, elevatedAssignmentRows]);

  const addRolZoekOpties = useMemo((): SearchableSelectOption[] => {
    const rows = overview?.by_role?.deelnemer ?? [];
    const seen = new Set<number>();
    const out: SearchableSelectOption[] = [];
    for (const m of rows) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push({
        value: String(m.id),
        label: `${m.first_name} ${m.last_name}`.trim(),
        description: m.email ?? undefined,
        group: "Nog Deelnemer",
      });
    }
    out.sort((a, b) => a.label.localeCompare(b.label, "nl"));
    return out;
  }, [overview?.by_role?.deelnemer]);

  const superAdmins = useMemo(() => users.filter((u) => u.is_super_admin), [users]);

  /** Eén entry per contactkaart (voor koppel-dropdown); overview kan meerdere rijen per kaart hebben. */
  const allMedewerkers = useMemo((): MedewerkerRolRij[] => {
    if (!overview) return [];
    const byContact = new Map<number, MedewerkerRolRij>();
    const keys = overview.roles?.map((r) => r.key) ?? ROL_UITLEG.map((r) => r.value);
    for (const key of keys) {
      for (const row of overview.by_role[key] ?? []) {
        if (!byContact.has(row.id)) byContact.set(row.id, row);
      }
    }
    return [...byContact.values()];
  }, [overview]);

  async function refreshTaken() {
    const res = await api.get<{ data: Taak[] }>("/api/taken");
    setTaken(res.data.data ?? []);
  }

  async function addTaak() {
    const name = newTaakNaam.trim();
    if (!name) return;
    setTakenBusy(true);
    setTakenError(null);
    try {
      await api.post("/api/taken", { name });
      setNewTaakNaam("");
      await refreshTaken();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Taak toevoegen mislukt";
      setTakenError(msg);
    } finally {
      setTakenBusy(false);
    }
  }

  async function saveTaakEdit(id: number) {
    const name = editTaakNaam.trim();
    if (!name) return;
    setTakenBusy(true);
    setTakenError(null);
    try {
      await api.put(`/api/taken/${id}`, { name });
      setEditTaakId(null);
      setEditTaakNaam("");
      await refreshTaken();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Taak wijzigen mislukt";
      setTakenError(msg);
    } finally {
      setTakenBusy(false);
    }
  }

  async function removeTaak(id: number, name: string) {
    if (!window.confirm(`Taak "${name}" verwijderen?`)) return;
    setTakenBusy(true);
    setTakenError(null);
    try {
      await api.delete(`/api/taken/${id}`);
      if (editTaakId === id) {
        setEditTaakId(null);
        setEditTaakNaam("");
      }
      await refreshTaken();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Taak verwijderen mislukt";
      setTakenError(msg);
    } finally {
      setTakenBusy(false);
    }
  }

  const linkMedewerkerZoekOpties = useMemo((): SearchableSelectOption[] => {
    return [
      { value: "", label: "— geen koppeling —", group: "Koppeling" },
      ...allMedewerkers.map((c) => ({
        value: String(c.id),
        label: `${c.first_name} ${c.last_name}`.trim(),
        description: c.email ?? undefined,
        group: "Medewerker-kaart",
      })),
    ];
  }, [allMedewerkers]);

  async function setRolVoorContact(contactId: number, app_role: AppRole) {
    await api.put(`/api/admin/contacts/${contactId}/app-role`, { app_role });
    await fetchData();
  }

  async function addMedewerkerMetRol() {
    const id = Number(addRolContactId);
    if (!Number.isFinite(id) || id <= 0) return;
    setAddRolBusy(true);
    try {
      await setRolVoorContact(id, addRolChoice);
      setAddRolContactId("");
    } finally {
      setAddRolBusy(false);
    }
  }

  async function handleUserAppRole(userId: number, app_role: AppRole) {
    await api.put(`/api/admin/users/${userId}/role`, { app_role });
    await fetchData();
  }

  async function handleLinkContact(userId: number, contactId: string) {
    await api.put(`/api/admin/users/${userId}/link`, {
      contact_id: contactId ? Number(contactId) : null,
    });
    await fetchData();
  }

  async function addExpectedUser(ev: FormEvent) {
    ev.preventDefault();
    setExpectedError(null);
    if (!expectedEmail.trim()) {
      setExpectedError("Vul een e-mailadres in (het adres waarmee ze straks bij Microsoft inloggen).");
      return;
    }
    setAddingExpected(true);
    try {
      await api.post("/api/admin/users/expected", {
        email: expectedEmail.trim(),
        name: expectedName.trim() || undefined,
        app_role: expectedAppRole,
      });
      setExpectedEmail("");
      setExpectedName("");
      setExpectedAppRole("deelnemer");
      await fetchData();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Toevoegen mislukt. Bestaat dit adres al?";
      setExpectedError(msg);
    } finally {
      setAddingExpected(false);
    }
  }

  async function removeExpectedUser(id: number) {
    if (!window.confirm("Dit verwachte account verwijderen? De persoon kan daarna alsnog gewoon inloggen; je moet de rol dan opnieuw instellen.")) {
      return;
    }
    try {
      await api.delete(`/api/admin/users/${id}`);
      await fetchData();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Verwijderen mislukt";
      window.alert(msg);
    }
  }

  /** Verwijdert een echt Microsoft-account uit `users` (superbeheerder). Contactkaarten blijven bestaan. */
  async function removeOAuthUserAccount(id: number, displayName: string) {
    if (
      !window.confirm(
        `Weet je zeker dat je het Microsoft-account “${displayName}” (database user #${id}) wilt verwijderen?\n\n` +
          "• De bijbehorende contactkaart(en) blijven bestaan; alleen de inlog-koppeling verdwijnt.\n" +
          "• Groeps-rollen van dit account (beheerder/kijker op groepen) gaan verloren.\n" +
          "• Records die deze gebruiker als aanmaker hadden, krijgen een andere gebruiker als aanmaker.\n" +
          "• Bij een volgende Microsoft-inlog wordt automatisch een nieuwe gebruikersrij aangemaakt.\n\n" +
          "Dit kan niet ongedaan worden gemaakt."
      )
    ) {
      return;
    }
    try {
      await api.post(`/api/admin/users/${id}/remove-account`);
      await fetchData();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; detail?: string } } };
      const d = ax.response?.data;
      const msg = [d?.error, d?.detail].filter(Boolean).join(" — ") || "Verwijderen mislukt";
      window.alert(msg);
    }
  }

  /** Verwijderen uit "hogere" rol: terug naar Deelnemer (per account als er een Microsoft-koppeling is). */
  async function verplaatsNaarDeelnemer(rij: MedewerkerRolRij) {
    if (rij.app_role === "deelnemer") return;
    if (rij.linked_user) {
      await handleUserAppRole(rij.linked_user.id, "deelnemer");
    } else {
      await setRolVoorContact(rij.id, "deelnemer");
    }
  }

  /** Rol in het blok wijzigen: kaart (alleen als nog geen apart account) of dit Microsoft-account. */
  async function wijzigRolInBlok(rij: MedewerkerRolRij, app_role: AppRole) {
    if (rij.linked_user) {
      await handleUserAppRole(rij.linked_user.id, app_role);
    } else {
      await setRolVoorContact(rij.id, app_role);
    }
  }

  if (loading || !overview) {
    return <div className="page"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title-row" style={{ marginBottom: 2 }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              {"Beheer"}
            </h2>
            <HelpHint label="Uitleg: gebruikers en app-rollen">
              Dit scherm is voor <strong>superbeheerder</strong>, <strong>beheerder</strong> (organisatie) en{" "}
              <strong>secretaris</strong>. De <strong>app-rol</strong> staat op de <strong>vrijwilliger- of personeelskaart</strong> in het
              adresboek; zo kun je iemand een rol geven vóórdat die ooit inlogt met Microsoft. Staat er <strong>één</strong> Microsoft-account
              op die kaart, dan geldt ook de rol van de kaart voor dat account. Zijn er <strong>twee of meer</strong> accounts op dezelfde kaart
              (bijv. werk en privé), dan heeft <strong>elk account een eigen app-rol</strong> — stel die in via het keuzemenu bij die rij of onder
              “Microsoft-accounts”. Groepsrollen (per <em>één groep</em>) stel je op <strong>Groepen</strong> in, niet hier.{" "}
              <strong>Superbeheerder</strong> kan onderaan een <strong>ingelogd</strong> Microsoft-account uit de database verwijderen
              via het <strong>pictogram</strong> (gebruiker met kruis; hover voor uitleg) — handig bij verkeerd account, test of dubbel; de kaart blijft bestaan en bij opnieuw inloggen ontstaat een nieuwe gebruikersrij.
            </HelpHint>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className={activeTab === "users" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("users")}
        >
          Gebruikers en rollen
        </button>
        <button
          type="button"
          className={activeTab === "taken" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("taken")}
        >
          Taken / takenlijst
        </button>
        <button
          type="button"
          className={activeTab === "backups" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("backups")}
        >
          Back-ups
        </button>
      </div>

      {activeTab === "users" && (
      <>
      <AdminRolesMatrixPanel isSuperAdmin={Boolean(me?.is_super_admin)} />

      {superAdmins.length > 0 && (
        <div className="detail-section" style={{ marginBottom: "1.5rem" }}>
          <h3 className="section-title">Superbeheerder</h3>
          <p style={{ margin: 0 }}>
            {superAdmins.map((u) => u.name).join(", ")}
          </p>
        </div>
      )}

      <div className="detail-section" style={{ marginBottom: "1.5rem" }}>
        <h3 className="section-title">
          Mensen en app-rollen{" "}
          <span className="data-list-row-muted">({combinedRows.length})</span>
        </h3>
        <p className="page-subtitle" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
          Eén lijst: Microsoft-accounts (ingelogd of verwacht) én medewerkers met een rol die nog niet inloggen.
          Koppel een kaart, wijzig de rol, of haal iemand terug naar Deelnemer. Superbeheerder kan een ingelogd account
          verwijderen; de kaart blijft bestaan.
        </p>
        <div
          className="add-row"
          style={{ marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}
        >
          <div style={{ flex: "1 1 14rem", minWidth: 200 }}>
            <label className="page-subtitle" style={{ display: "block", marginBottom: 4 }}>
              Medewerker een rol geven
            </label>
            <SearchableSelect
              value={addRolContactId}
              onValueChange={setAddRolContactId}
              options={addRolZoekOpties}
              placeholder="Zoek in het adresboek…"
              emptyMessage="Geen medewerker gevonden, of iedereen heeft al een rol in deze lijst."
              aria-label="Medewerker kiezen om een rol te geven"
            />
          </div>
          <div>
            <label className="page-subtitle" style={{ display: "block", marginBottom: 4 }}>
              Rol
            </label>
            <select
              className="filter-select"
              value={addRolChoice}
              onChange={(e) => setAddRolChoice(e.target.value as AppRole)}
            >
              {rolesToAssign.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={!addRolContactId || addRolBusy}
            onClick={() => void addMedewerkerMetRol()}
          >
            {addRolBusy ? "Bezig…" : "Toevoegen"}
          </button>
        </div>
        <form
          className="add-row"
          style={{ marginBottom: "1.25rem", flexWrap: "wrap" as const, gap: "0.5rem", alignItems: "flex-end" }}
          onSubmit={addExpectedUser}
        >
          <div>
            <label className="page-subtitle" style={{ display: "block", marginBottom: 4 }}>
              E-mail (Microsoft / Entra)
            </label>
            <input
              type="email"
              className="filter-select"
              style={{ minWidth: 260 }}
              value={expectedEmail}
              onChange={(e) => setExpectedEmail(e.target.value)}
              placeholder="naam@organisatie.nl"
              autoComplete="off"
              required
            />
          </div>
          <div>
            <label className="page-subtitle" style={{ display: "block", marginBottom: 4 }}>
              Weergavenaam (optioneel)
            </label>
            <input
              type="text"
              className="filter-select"
              style={{ minWidth: 180 }}
              value={expectedName}
              onChange={(e) => setExpectedName(e.target.value)}
              placeholder="Tot eerste inlog: placeholder"
            />
          </div>
          <div>
            <label className="page-subtitle" style={{ display: "block", marginBottom: 4 }}>
              App-rol (tot koppeling met kaart)
            </label>
            <select
              className="filter-select"
              value={expectedAppRole}
              onChange={(e) => setExpectedAppRole(e.target.value as AppRole)}
            >
              {APP_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn-primary" disabled={addingExpected}>
            {addingExpected ? "Bezig…" : "Verwacht account toevoegen"}
          </button>
        </form>
        {expectedError && (
          <p className="page-subtitle" style={{ color: "var(--danger, #b00020)", marginTop: 0, marginBottom: "1rem" }}>
            {expectedError}
          </p>
        )}
        <div className="table-wrap--scroll" style={{ marginTop: "0.5rem" }}>
        <table className="users-table users-table--compact" style={{ marginBottom: 0, tableLayout: "fixed" as const }}>
          <colgroup>
            <col style={{ width: "3rem" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "13rem" }} />
            <col style={{ width: "7rem" }} />
            <col />
            <col style={{ width: "5.5rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th title="Avatar">Av.</th>
              <th>Naam</th>
              <th>E-mail</th>
              <th>App-rol</th>
              <th>Status</th>
              <th title="Gekoppelde vrijwilliger- of personeelskaart">Contact</th>
              <th className="users-table__th-actions">Acties</th>
            </tr>
          </thead>
          <tbody>
            {combinedRows.map((row) => {
              if (row.kind === "staff") {
                const m = row.staff;
                const initialen = `${m.first_name[0] ?? ""}${m.last_name[0] ?? ""}`.toUpperCase();
                return (
                  <tr key={m.row_key ?? `c-${m.id}`}>
                    <td>
                      <div className="member-avatar"><span>{initialen}</span></div>
                    </td>
                    <td className="member-name">{m.first_name} {m.last_name}</td>
                    <td className="member-email">{m.email ?? "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Terug naar Deelnemer"
                          aria-label={`Deelnemer: ${m.first_name} ${m.last_name}`}
                          onClick={() => void verplaatsNaarDeelnemer(m)}
                        >
                          <SvgUserMinus />
                        </button>
                        <select
                          className="filter-select"
                          style={{ width: "100%", maxWidth: "100%" }}
                          value={m.app_role}
                          onChange={(e) => void wijzigRolInBlok(m, e.target.value as AppRole)}
                        >
                          {APP_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td><span className="data-list-row-muted">Alleen kaart</span></td>
                    <td className="data-list-row-muted">Medewerkerkaart, nog geen inlog</td>
                    <td />
                  </tr>
                );
              }
              const u = row.user;
              const pending = isPendingMicrosoftUserOid(u.microsoft_oid);
              const role = (row.staff?.app_role ?? u.app_role ?? "deelnemer") as AppRole;
              const initialen =
                pending && u.email
                  ? u.email.slice(0, 2).toUpperCase()
                  : u.name.split(" ").map((n) => n[0]).join("").slice(0, 2);
              const isSelf = me != null && u.id === me.id;
              const isSuperRow = Boolean(u.is_super_admin);
              const canRemoveOAuth = Boolean(me?.is_super_admin) && !isSuperRow && !isSelf && !pending;
              return (
                <tr key={`u-${u.id}`} className="user-row">
                  <td>
                    <div className="member-avatar">
                      {u.avatar_url ? <img src={u.avatar_url} alt={u.name} /> : <span>{initialen}</span>}
                    </div>
                  </td>
                  <td className="member-name">
                    {u.name}
                    {row.staff && `${row.staff.first_name} ${row.staff.last_name}`.trim() !== u.name.trim() && (
                      <span className="data-list-row-muted" style={{ display: "block", fontSize: "0.85em" }}>
                        Kaart: {row.staff.first_name} {row.staff.last_name}
                      </span>
                    )}
                  </td>
                  <td className="member-email" style={{ overflow: "hidden", textOverflow: "ellipsis" }} title={u.email}>
                    {u.email}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {!isSuperRow && role !== "deelnemer" && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Terug naar Deelnemer"
                          aria-label={`Deelnemer: ${u.name}`}
                          onClick={() => void handleUserAppRole(u.id, "deelnemer")}
                        >
                          <SvgUserMinus />
                        </button>
                      )}
                      <select
                        className="filter-select"
                        style={{ width: "100%", maxWidth: "100%" }}
                        value={role}
                        onChange={(e) => void handleUserAppRole(u.id, e.target.value as AppRole)}
                        disabled={isSuperRow}
                      >
                        {APP_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    {isSuperRow ? (
                      <span className="badge-you" style={{ background: "var(--primary)", color: "white" }}>Superbeheerder</span>
                    ) : pending ? (
                      <span className="badge-you" style={{ background: "var(--text-muted, #666)", color: "white" }}>Verwacht</span>
                    ) : (
                      <span className="data-list-row-muted">Ingelogd</span>
                    )}
                  </td>
                  <td className="users-table__td-contact">
                    <SearchableSelect
                      className="users-table-contact-select"
                      value={u.contact_id != null ? String(u.contact_id) : ""}
                      onValueChange={(v) => { void handleLinkContact(u.id, v); }}
                      options={linkMedewerkerZoekOpties}
                      placeholder="Zoek contact…"
                      emptyMessage="Geen contactkaart gevonden."
                      aria-label={`Gekoppeld contact voor ${u.name}`}
                    />
                  </td>
                  <td className="users-table__td-actions">
                    <div className="users-table__action-btns">
                      {pending && !isSuperRow && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Verwacht account verwijderen"
                          aria-label={`Verwacht account verwijderen: ${u.name}`}
                          onClick={() => void removeExpectedUser(u.id)}
                        >
                          <SvgTrash />
                        </button>
                      )}
                      {canRemoveOAuth && (
                        <button
                          type="button"
                          className="icon-btn icon-btn--danger"
                          title="Microsoft-account uit database verwijderen (kaart blijft bestaan)"
                          aria-label={`Account uit database: ${u.name}`}
                          onClick={() => void removeOAuthUserAccount(u.id, u.name)}
                        >
                          <SvgUserOff />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      </>
      )}

      {activeTab === "taken" && (
        <div className="detail-section">
          <h3 className="section-title">Takenlijst beheren</h3>
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            Voeg taken toe, hernoem ze of verwijder ze. Verwijderen kan alleen als de taak niet meer aan groepsleden hangt.
          </p>
          <div className="add-row" style={{ gap: 8, marginBottom: "0.9rem", flexWrap: "wrap" }}>
            <input
              type="text"
              className="filter-select"
              style={{ minWidth: 280 }}
              placeholder="Nieuwe taaknaam…"
              value={newTaakNaam}
              onChange={(e) => setNewTaakNaam(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void addTaak();
                }
              }}
            />
            <button type="button" className="btn-primary" disabled={takenBusy || !newTaakNaam.trim()} onClick={() => void addTaak()}>
              Toevoegen
            </button>
          </div>
          {takenError && (
            <p className="page-subtitle" style={{ color: "var(--danger, #b00020)", marginTop: 0 }}>
              {takenError}
            </p>
          )}
          <div className="table-wrap--scroll">
            <table className="users-table users-table--compact" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Naam</th>
                  <th style={{ width: "11rem" }}>Leden</th>
                  <th style={{ width: "5.5rem" }}>Acties</th>
                </tr>
              </thead>
              <tbody>
                {taken.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {editTaakId === t.id ? (
                        <input
                          type="text"
                          className="filter-select"
                          value={editTaakNaam}
                          onChange={(e) => setEditTaakNaam(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void saveTaakEdit(t.id);
                            }
                          }}
                        />
                      ) : (
                        t.name
                      )}
                    </td>
                    <td className="data-list-row-muted" title="Aantal contacten met deze taak (in één of meer groepen)">
                      {t.member_count ?? 0}
                    </td>
                    <td className="users-table__td-actions">
                      {editTaakId === t.id ? (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={takenBusy || !editTaakNaam.trim()}
                            onClick={() => void saveTaakEdit(t.id)}
                          >
                            Opslaan
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            disabled={takenBusy}
                            onClick={() => {
                              setEditTaakId(null);
                              setEditTaakNaam("");
                            }}
                          >
                            Annuleren
                          </button>
                        </div>
                      ) : (
                        <div className="users-table__action-btns">
                          <button
                            type="button"
                            className="btn-icon"
                            disabled={takenBusy}
                            title="Wijzigen"
                            aria-label={`Taak wijzigen: ${t.name}`}
                            onClick={() => {
                              setEditTaakId(t.id);
                              setEditTaakNaam(t.name);
                            }}
                          >
                            <SvgEdit />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-icon-danger"
                            disabled={takenBusy}
                            title="Verwijderen"
                            aria-label={`Taak verwijderen: ${t.name}`}
                            onClick={() => void removeTaak(t.id, t.name)}
                          >
                            <SvgTrashCard />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {taken.length === 0 && (
                  <tr>
                    <td colSpan={3} className="data-list-row-muted">
                      Geen taken gevonden.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "backups" && <BeheerBackupsPanel user={me} />}
    </div>
  );
}
